import { compareRanks } from './lexorank'

export interface SpaceRank {
  projectId: string
  sortOrder: string
}

export interface SortableSpace {
  id: string
  name: string
}

const byName = (a: SortableSpace, b: SortableSpace) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

/**
 * Orders the sidebar's root Spaces from the current user's private rank rows.
 *
 * - No rank rows at all → plain A–Z by name. That's the behaviour Spaces had
 *   before MOL-9, and it's what every user sees until their first reorder.
 * - Ranked Spaces lead, in LexoRank order.
 * - Unranked Spaces (created since the last reorder, or restored after a delete
 *   retired their row) follow, A–Z among themselves.
 *
 * Pure so the ordering contract can be tested without RxDB.
 */
export function sortSpacesByPrivateOrder<T extends SortableSpace>(
  spaces: Array<T>,
  ranks: Array<SpaceRank>,
): Array<T> {
  const rankByProjectId = new Map(ranks.map((rank) => [rank.projectId, rank.sortOrder]))

  const ranked: Array<T> = []
  const unranked: Array<T> = []
  for (const space of spaces) {
    if (rankByProjectId.has(space.id)) ranked.push(space)
    else unranked.push(space)
  }

  ranked.sort((a, b) => {
    const comparison = compareRanks(rankByProjectId.get(a.id), rankByProjectId.get(b.id))
    // Two rows can briefly share a rank while a rebalance replicates. Fall back
    // to name so the list doesn't flicker between two arbitrary orders.
    return comparison !== 0 ? comparison : byName(a, b)
  })
  unranked.sort(byName)

  return [...ranked, ...unranked]
}
